import { getFilterVariables } from '@kizen/filters/variables';
import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { actions } from './../store';
import { useDefaultModelQuery } from 'queries/models/custom-objects';
import { selectFilterMetadataDefinition } from 'store/filterMetaData/selectors';
import { useSelector } from 'react-redux';
import { camelToSnakeCaseKeys } from 'services/helpers';
import { loadSavedFilter } from 'ts-filters/utils';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { isCustomFilter, isInGroupFilter } from 'ts-filters/legacy';
import { descriptionChanged } from './utils';
import { isLLMDecisionFilter } from '__components/Wizards/MetaFilters/types';

export const conditionalAddToString = (condition, str) =>
  condition ? str : '';

const filterSet = ({ isOnlySet, filterSetOperation, filters }) => {
  const setText = filters.reduce((acc, { description }, index) => {
    acc += conditionalAddToString(
      index !== 0,
      filterSetOperation === 'all' ? 'AND ' : 'OR '
    );
    return acc + description;
  }, '');
  return isOnlySet ? setText : `(${setText})`;
};

export const filterText = (filterConfig) => {
  if (!filterConfig) {
    return '';
  }

  const { query } = filterConfig;

  return query.reduce((acc, { and, filters }, index) => {
    const seperator = conditionalAddToString(
      index !== 0,
      filterConfig.and ? ' AND ' : ' OR '
    );
    acc += seperator;
    acc += filterSet({
      isOnlySet: query.length === 1,
      filterSetOperation: and ? 'all' : 'any',
      filters,
    });
    return acc;
  }, '');
};

export const getConditionDescription = (details, entityName, t) => {
  if (isCustomFilter(details.type)) {
    return [entityName, filterText(details.filterConfig)]
      .filter(Boolean)
      .join(' ');
  }
  if (isLLMDecisionFilter(details.type)) {
    return [entityName, t('LLM Decision')].filter(Boolean).join(' ');
  }
  const groups = details.groups || [];
  const groupNames = groups.length
    ? groups
        .map(
          ({ name, deleted }) =>
            `‘${name || '—'}${deleted ? ` [${t('Deleted')}]` : ''}’`
        )
        .join(', ')
    : t("'[Deleted]'");

  return isInGroupFilter(details.type)
    ? `${entityName} ${t('In Group(s)')} ${groupNames}`
    : `${entityName} ${t('Not In Group(s)')} ${groupNames}`;
};

export const addFilterDesciption = (filters) =>
  filters.map((filter) => filter.toString());

export const useAddFilterDescriptions = (
  customObject = {},
  conditionDescriptionQue,
  steps,
  dispatch
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const metaData = useSelector(selectFilterMetadataDefinition);
  const businessClientObject = useSelector(getBusinessClientObject);
  const { data: clientObject } = useDefaultModelQuery(businessClientObject.id);

  const firstStepProcessed = useRef(false);
  const [que, setQue] = useState(conditionDescriptionQue);
  const queIntialized = useRef(false);

  if (conditionDescriptionQue.length && !queIntialized.current) {
    queIntialized.current = true;
    setQue(conditionDescriptionQue);
  }

  const filterVariablesObject = useMemo(() => {
    if (metaData && customObject && clientObject) {
      return Object.fromEntries(
        getFilterVariables(
          customObject.name === 'client_client' ? clientObject : customObject,
          { fieldSettingsSearch: true }
        )
      );
    }
    return null;
  }, [metaData, customObject, clientObject]);

  const processConditionStep = useCallback(
    async (step) => {
      const { details } = step;
      let newDetails = details;

      if (isCustomFilter(details.type) && filterVariablesObject) {
        const descriptions = [];
        const config = await loadSavedFilter(
          metaData,
          camelToSnakeCaseKeys(details.filterConfig),
          filterVariablesObject,
          queryClient
        );

        if (Array.isArray(config)) {
          config.forEach((filterSet) => {
            const filterDescriptions = addFilterDesciption(
              filterSet.filters,
              step
            );
            descriptions.push(filterDescriptions);
          });
        } else {
          const filterDescriptions = addFilterDesciption(config.filters);
          descriptions.push(filterDescriptions);
        }

        const newQuery = details.filterConfig.query.map((query, queryIndex) => {
          const filters = query.filters.map((filter, filterIndex, array) => {
            return {
              ...filter,
              description: `${descriptions[queryIndex][filterIndex]}${
                filterIndex !== array.length - 1 ? ' ' : ''
              }`,
            };
          });
          return { ...query, filters };
        });

        newDetails = {
          ...details,
          filterConfig: {
            ...details.filterConfig,
            query: newQuery,
          },
        };
      }
      // groups

      const description = getConditionDescription(
        newDetails,
        customObject?.entityName,
        t
      );

      const queName = 'conditionDescriptionQue';

      if (descriptionChanged(description, step)) {
        dispatch(
          actions.updateStep({
            step: { ...step, details: newDetails, description },
            dirty: false,
          })
        );
      }

      setQue((prevQue) => {
        const nextQue = prevQue.filter((id) => id !== step.id);

        if (nextQue.length === 0) {
          dispatch(
            actions.clearDescriptionQue({
              queName,
            })
          );
        }
        return nextQue;
      });
    },
    [
      filterVariablesObject,
      customObject?.entityName,
      t,
      metaData,
      queryClient,
      dispatch,
    ]
  );

  const stepToProcess =
    que.length > 0 && steps.length > 0 && filterVariablesObject
      ? steps.find((s) => s.id === que[0])
      : undefined;

  useEffect(() => {
    let id;

    if (stepToProcess) {
      if (!firstStepProcessed.current) {
        id = setTimeout(() => {
          firstStepProcessed.current = true;
          processConditionStep(stepToProcess);
        }, 1000);
      } else {
        processConditionStep(stepToProcess);
      }
    }
    return () => clearTimeout(id);
  }, [processConditionStep, stepToProcess]);

  return {
    clientObject,
    metaData,
    filterVariablesObject,
    processConditionStep,
  };
};
